<template>
  <Filters />
  <Loader v-if="isLoading" class="mt-20 mb-40" :content="true" />
  <Schedule
    v-else
    :key="forceRerender"
    class="mt-5"
    :theads="['monday', 'sunday']"
    :table-time="tableTime"
    :events="events"
    @viewEvent="viewEvent"
    @editEvent="editEvent"
    @deleteEvent="toggleConfirmationModal"
  />
  <AddEditRoutineModal
    :modal-show="showModal"
    :edit-data="editEventData"
    :get-subjects-rooms-for-class="getSubjectsRoomsForClass"
    :get-teachers-for-subject="getTeachersForSubject"
    @close="handleModalClose"
    @saved="handleRoutineSaved"
  />

  <UIModalContainer
    footer-classes="flex justify-end"
    :modal-show="routinesDetailModal"
    :modal-width="40"
    @handleClick="handleRoutinesDetailClick"
  >
    <template v-slot:header>
      <span v-i18n="routine">PER_MGR_DETAILS</span>
    </template>
    <template v-slot>
      <div v-for="event in detailsEventsArr" :key="event.id" class="pb-8">
        <div class="flex flex-col gap-5 sm:flex-row">
          <span class="text-2xl text-label-text font-bold">
            {{ event?.title || GENERAL_CONSTANTS.NOT_APPLICABLE }}
          </span>
          <span class="subjectColor"></span>
        </div>
        <div class="flex flex-1 justify-between">
          <div class="py-3 flex flex-1 items-center">
            <Icon icon="calender" color="primary-green" height="21" width="18" />
            <span class="px-2 md:px-3 text-xs md:text-base text-primary-grey-light capitalize">
              {{ event?.day || GENERAL_CONSTANTS.NOT_APPLICABLE }}
            </span>
          </div>
          <div class="py-3 flex flex-1 clock-icon items-center">
            <Icon icon="clock" color="primary-green" height="21" width="18" />
            <span class="px-2 md:px-3 text-xs md:text-base text-primary-grey-light">
              {{ localTime(event.startTime) }} - {{ localTime(event.endTime) }}
            </span>
          </div>
        </div>

        <div class="flex flex-1 justify-between">
          <div class="flex flex-1 py-3">
            <div class="text-xs md:text-base text-label-text font-bold rtl:text-right">
              <span v-i18n="routine">Room</span>
              :
              <span class="md:px-3 text-primary-grey-light font-normal">
                {{ event?.room || GENERAL_CONSTANTS.NOT_APPLICABLE }}
              </span>
            </div>
          </div>
          <div class="flex flex-1 py-3">
            <div class="text-xs md:text-base text-label-text font-bold rtl:text-right">
              <span v-i18n="routine">Subject</span>
              :
              <span class="md:px-3 text-primary-grey-light font-normal">
                {{ event?.subject?.title || GENERAL_CONSTANTS.NOT_APPLICABLE }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex flex-1 justify-between">
          <div class="flex flex-1 py-3">
            <div class="text-xs md:text-base text-label-text font-bold rtl:text-right">
              <span v-i18n="routine">Teacher</span>
              :
              <span class="md:px-3 text-primary-grey-light font-normal">
                {{ event?.teacher || GENERAL_CONSTANTS.NOT_APPLICABLE }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </UIModalContainer>
  <UIConfirmationModal
    heading="DELETE_TIME_TABLE_AUTOMATOR"
    message="Are you sure you want to delete this "
    button-text="Delete"
    :modal="confirmation"
    :name="currentEvent?.title"
    remaining-message=" ?"
    @cancel="toggleConfirmationModal"
    @confirm="deleteEvent"
  />
</template>

<script>
import { isEmpty } from 'lodash'
import Filters from '@views/time-table/time-table-automator/PeriodManagerFilters.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import UIModalContainer from '@components/UiElements/UiModalContainer.vue'
import Schedule from '@components/UiElements/Scheduler/Schedule.vue'
import { mapActions, mapState } from 'vuex'
import Icon from '@components/icons/icon.vue'
import { getSubjectEvent, getTimeRange } from '@utils/timeTableUtils'
import DATE_TIME from '@src/constants/date-time-constants.js'
import timeMixin from '@src/mixins/timeMixin'
import GENERAL_CONSTANTS from '@src/constants/general-constants.js'
import { objectDeepCopy } from '@utils/generalUtil'
import UIConfirmationModal from '@src/components/UiElements/UIConfirmationModal.vue'
import AddEditRoutineModal from '@views/time-table/time-table-automator/AddEditRoutineModal.vue'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import { buildWhereQuery } from '@src/utils/filters/index.js'
import { fullName } from '@src/utils/settings/tenant-user.util.js'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'

export default {
  name: 'TimeTableAutomatorView',
  components: {
    Loader,
    Schedule,
    Icon,
    UIModalContainer,
    Filters,
    UIConfirmationModal,
    AddEditRoutineModal,
  },
  mixins: [timeMixin],
  data() {
    return {
      GENERAL_CONSTANTS,
      DATE_TIME,
      isEmpty,
      currentEvent: null,
      confirmation: false,
      isLoading: false,
      forceRerender: 0,
      timeRange: ['0:00 am', '0:00 am'],
      events: [],
      respDataObjDump: null,
      routinesDetailModal: false,
      detailsEventsArr: [],
      editEventData: null,
      routine: 'routine',
    }
  },
  page: {
    title: 'Schedule Manager',
    meta: [
      {
        name: 'description',
        content: 'Manage  schedule of class ',
      },
    ],
  },
  computed: {
    ...mapState({
      activeRole: (state) => state.layout.activeRole,
      currentUser: (state) => state.auth.currentUser,
      showModal: (state) => state.layout.showModal,
      currentSectionId: (state) => state.layout.currentSectionScope.id,
    }),
    tableTime() {
      return [
        this.timeRange[0] || this.DATE_TIME.dayStartTime,
        this.timeRange[1] || this.DATE_TIME.dayEndTime,
      ]
    },
  },
  watch: {
    currentSectionId: {
      deep: true,
      handler() {
        if (this.currentSectionId) this.loadRightBarStats()
        else this.resetComponent()
      },
    },
    events: {
      deep: true,
      immediate: true,
      handler() {
        const temp = getTimeRange(this.events)
        this.timeRange[0] = temp[0]
        this.timeRange[1] = temp[1]
      },
    },
  },
  created() {
    this.setRightbarContent({
      header: {
        title: 'SCHED_MGR',
        buttons: [
          {
            title: 'ADD_TIME_TABLE_MDR',
            classes: ['primary-button-right'],
            action: { name: 'layout/setShowModal', payload: {} },
          },
        ],
      },
      stats: [
        {
          title: this.$t('routine.Subject'),
          categories: [
            { text: 'Total Subject', value: '--' },
            { text: `Todays Subject`, value: '--' },
          ],
        },
      ],
    })
    if (this.currentSectionId) this.loadRightBarStats()
  },
  beforeUnmount() {
    this.viewEvent({})
  },
  methods: {
    resetComponent() {
      Object.assign(this.$data, {
        GENERAL_CONSTANTS,
        DATE_TIME,
        isEmpty,
        currentEvent: null,
        confirmation: false,
        isLoading: false,
        forceRerender: 0,
        timeRange: ['0:00 am', '0:00 am'],
        events: [],
        respDataObjDump: null,
        routinesDetailModal: false,
        detailsEventsArr: [],
        editEventData: null,
        routine: 'routine',
      })
      this.setRightbarContent({
        header: {
          title: 'SCHED_MGR',
          buttons: [
            {
              title: 'ADD_TIME_TABLE_MDR',
              classes: ['primary-button-right'],
              action: { name: 'layout/setShowModal', payload: {} },
            },
          ],
        },
        stats: [
          {
            title: GENERAL_CONSTANTS.STATS_AND_SUMMARY,
            categories: [
              { text: 'Total Subject', value: '--' },
              { text: `Todays Subject`, value: '--' },
            ],
          },
        ],
      })
    },
    applyFilter() {
      const data = {
        section_id: this.currentSectionId,
      }
      this.filterRoutine(data).then((resp) => {
        this.applyRoutineFilter(resp)
        this.forceRerender++
      })
    },
    resetFilter() {
      const data = {
        section_id: this.currentSectionId,
      }
      this.filterRoutine(data).then((resp) => {
        this.applyRoutineFilter(resp)
      })
    },
    applyRoutineFilter(resp) {
      const response = objectDeepCopy(resp)
      this.events = []
      const totalSubject = response?.data?.right_bar?.total_subjects
      const todaysPeriods = response?.data?.right_bar?.today_periods
      const totalPeriodManagers = response?.data?.right_bar?.subjects_with_periods
      response.data.records.forEach((item) => {
        this.events.push(getSubjectEvent(item))
      })
      this.respDataObjDump = response.data.records
      this.setRightbarContent({
        header: {
          title: 'SCHED_MGR',
          buttons: [
            {
              title: 'ADD_TIME_TABLE_MDR',
              classes: ['primary-button-right'],
              action: { name: 'layout/setShowModal', payload: {} },
            },
          ],
        },
        stats: [
          {
            title: this.$t('routine.Subject'),
            categories: [
              {
                text: 'Total Subject',
                value: totalSubject,
                tooltipDescription: 'Total no of Subject',
              },
              {
                text: 'TOTAL_P_MANAGER',
                value: totalPeriodManagers,
                tooltipDescription: 'Total Period Manager',
              },
              {
                text: 'TODAY_PERIODS',
                value: todaysPeriods,
                tooltipDescription: 'Period held today',
              },
            ],
          },
        ],
      })
    },

    handleRoutinesDetailClick(eventType) {
      if (eventType === 'close') this.routinesDetailModal = false
    },

    handleModalClose() {
      this.editEventData = null
    },

    handleRoutineSaved() {
      this.applyFilter()
      this.editEventData = null
    },

    viewEvent(eventId) {
      let detailsEventsArr = []
      const event = this.events.find((event) => event.id === eventId)
      if (event) {
        detailsEventsArr.push(event)

        if (event?.isOverlapped) {
          const { overlappedIds } = event || {}
          const allOverlappedEvents = overlappedIds.map((id) => {
            return getSubjectEvent(this.respDataObjDump.find((obj) => obj.id === id))
          })

          detailsEventsArr = [...detailsEventsArr, ...allOverlappedEvents]
        }
        this.detailsEventsArr = detailsEventsArr
        this.routinesDetailModal = true
      }
    },

    editEvent(event) {
      this.editEventData = event
      this.$store.dispatch('layout/setShowModal', true)
    },
    toggleConfirmationModal(event) {
      this.currentEvent = event
      this.confirmation = !this.confirmation
      this.$store.dispatch('routine/setShowRoutineDeleteModal', this.confirmation)
      if (!this.confirmation) this.currentEvent = null
    },
    deleteEvent() {
      const { id } = this.currentEvent

      this.deactivateRoutine(id).then(() => {
        this.$store.commit('toast/NEW', {
          type: 'success',
          message: this.$t(`toast.Routine Deactivated!`),
        })
        this.applyFilter()
        this.toggleConfirmationModal()
      })

      this.viewEvent({})
    },

    loadRightBarStats() {
      const query = {
        section_id: this.currentSectionId,
      }
      this.isLoading = true
      this.filterRoutine(query)
        .then((resp) => {
          this.applyRoutineFilter(resp)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    getSubjectsRoomsForClass(searchQuery, currentSectionId) {
      let teacherPayload
      if (this.activeRole === TENANT_ROLES.SECTION_TEACHER) {
        teacherPayload = {
          ...buildWhereQuery(FILTER_KEYS.IN, 'assigned_teachers_ids', [this.currentUser?.id]),
        }
      }
      return new Promise((resolve) => {
        const query = {
          $where: {
            ...teacherPayload,
            ...buildWhereQuery(FILTER_KEYS.EQUAL, 'section_id', currentSectionId),
            ...buildWhereQuery(FILTER_KEYS.ILIKE, 'title', searchQuery),
          },
        }
        const subjects = this.classSubjects(query).then((response) => {
          return objectDeepCopy(response.data.records)
        })
        const rooms = this.getRoomsForClass(query)
        Promise.all([subjects, rooms]).then((values) => {
          resolve({
            subjects: values[0],
            rooms: values[1].data,
          })
        })
      })
    },

    getTeachersForSubject(searchQuery) {
      if (this.activeRole === TENANT_ROLES.SECTION_TEACHER) {
        return Promise.resolve({ teachers: [] })
      }

      const payload = {
        $where: {
          ...buildWhereQuery(FILTER_KEYS.ILIKE, 'search', searchQuery),
        },
      }
      return new Promise((resolve) => {
        this.getSectionSubjectTeacher(payload).then((response) => {
          const teachers = objectDeepCopy(response.data.records)
          teachers.forEach((teacher) => {
            fullName(teacher)
          })
          resolve({ teachers })
        })
      })
    },
    ...mapActions('layout', ['setRightbarContent']),
    ...mapActions('period', ['getSectionSubjectTeacher']),
    ...mapActions('routine', ['filterRoutine', 'deactivateRoutine', 'getRoomsForClass']),
    ...mapActions('subjects', ['classSubjects']),
  },
}
</script>

<style lang="scss">
.vs__dropdown-toggle {
  height: inherit;
}

.vs--single.vs--open .vs__selected {
  position: relative !important;
}

.vs__dropdown-menu {
  right: 0 !important;
  left: auto !important;
}
</style>
